import { Link } from "react-router-dom";
import {
    Trophy,
    Target,
    Users,
    BookOpen,
    Award,
    Clock,
    Star,
    Quote,
    MapPin,
    Phone,
    Mail,
    ChevronRight,
    Shield,
    GraduationCap,
    BadgeCheck,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import EnquiryForm from "@/components/EnquiryForm";
import useSEO from "@/hooks/useSEO";
import { COURSES, PRIMARY_COURSES } from "@/data/courses";
import { SITE, telLink, mailLink, waLink } from "@/data/site";

const STATS = [
    { num: "12+", label: "Years of Excellence" },
    { num: "100+", label: "Defence Selections" },
    { num: "500+", label: "NEET / JEE Qualifiers" },
    { num: "20", label: "Avg Batch Size" },
];

const WHY_US = [
    {
        icon: Shield,
        title: "Ex-Defence Officer Mentors",
        body: "Veterans from the Indian Army and Air Force lead our SSB and NDA prep — real boards, real wisdom.",
    },
    {
        icon: Target,
        title: "NTA-Pattern Mocks Weekly",
        body: "Over 30 full-length tests a year for NEET/JEE/AISSEE — with all-India ranking and granular analytics.",
    },
    {
        icon: Users,
        title: "20-Student Batches Only",
        body: "We cap classroom strength so every student gets eye contact and personalised doubt support.",
    },
    {
        icon: BookOpen,
        title: "NCERT-Rooted Curriculum",
        body: "Especially for NEET — every chapter is anchored to NCERT first, advanced practice second.",
    },
    {
        icon: Award,
        title: "100+ Selections Track Record",
        body: "Sainik School, RIMC, RMS, NDA, NEET, JEE — our results speak louder than any brochure.",
    },
    {
        icon: Clock,
        title: "Daily Doubt Clinics",
        body: "Every evening, every subject — clear today's doubt before tomorrow's lecture.",
    },
];

const TESTIMONIALS = [
    {
        name: "Arjun Sharma",
        result: "Selected, Sainik School Kapurthala",
        text: "The mentors at Dacron didn't just teach me Maths — they taught me discipline. The mock interviews gave me real confidence on the day of the AISSEE board.",
    },
    {
        name: "Pooja Kaur",
        result: "NEET 2025 - 645/720",
        text: "I shifted to Dacron in Class 12. NCERT focus, weekly tests and the personal mentor call every Sunday changed my prep entirely.",
    },
    {
        name: "Capt. R. Singh (Parent)",
        result: "Son cleared NDA written + SSB",
        text: "As a serving officer, I had high standards. Dacron's SSB workshop is on par with what I've seen in officer institutions. Top recommendation.",
    },
    {
        name: "Manvir Verma",
        result: "JEE Main 2025 - 99.1 percentile",
        text: "Faculty from IIT/NIT background, weekly mocks, and the doubt portal at midnight — Pathankot finally has a JEE coaching that matches Kota.",
    },
];

const REVIEWS = [
    {
        name: "Reena Bhatia",
        stars: 5,
        text: "Best coaching for Sainik School in Pathankot. My son got selected in his first attempt. Highly recommended!",
    },
    {
        name: "Suresh Kumar",
        stars: 5,
        text: "Excellent NEET coaching. Faculty is very supportive, mock tests are tough and the analysis report is detailed.",
    },
    {
        name: "Priya Mahajan",
        stars: 5,
        text: "Wonderful tuition centre. My daughter's marks jumped from 72% to 91% in just 8 months. Thank you Dacron.",
    },
    {
        name: "Harjit Singh",
        stars: 5,
        text: "Defence coaching is outstanding. SSB workshop was a game changer for my son. Got recommended in his first SSB.",
    },
];

const FAQS = [
    {
        q: "Which is the best coaching institute in Pathankot?",
        a: "Dacron Classes is widely rated as the best coaching institute in Pathankot for defence (NDA, Sainik, RIMC, RMS), medical (NEET), engineering (JEE), and school tuitions (Class 6-12). With 100+ defence selections, ex-officer mentors, NTA-pattern mocks and small batch sizes, parents across Punjab choose Dacron for their child's future.",
    },
    {
        q: "Do you provide a free demo class?",
        a: "Yes. We offer a 100% free demo class for every course. Just fill the enquiry form or WhatsApp us — our counsellor will book a slot at your convenience.",
    },
    {
        q: "What is the fee structure?",
        a: "Our fee depends on the course, class and duration. We are transparent and competitive — please call +91 82640 31180 or fill the enquiry form to receive the latest fee chart.",
    },
    {
        q: "Where is Dacron Classes located in Pathankot?",
        a: "We are located at Main Bazar Road, Near Nangal Chowk, Pathankot, Punjab 145001. Easy parking, walking distance from major schools and bus stops.",
    },
    {
        q: "Do you offer hostel or transport facility?",
        a: "We are a day-coaching institute. We have tie-ups with safe local PG accommodations for outstation students, and the institute is well-served by city auto-rickshaw routes.",
    },
    {
        q: "How is Dacron different from other coaching institutes?",
        a: "Three things — (1) Officer-led mentorship for defence aspirants, (2) NCERT-rooted teaching for NEET, (3) IIT/NIT alumni faculty for JEE — combined with 20-student batch caps and daily doubt clinics. We pair Kota-quality content with hometown personalised attention.",
    },
];

export default function HomePage() {
    useSEO({
        title:
            "Dacron Classes - Best Coaching Institute in Pathankot | NDA, Sainik School, NEET, JEE",
        description:
            "Pathankot's #1 coaching institute. Sainik School, NDA, RIMC, RMS, NEET, JEE, Foundation & Tuition (Class 6-12). 100+ Selections. Book a Free Demo Class today.",
        keywords:
            "Best coaching institute in Pathankot, Best Sainik School Coaching in Pathankot, Best NDA Coaching in Pathankot, Top Tuition Centre in Pathankot, Best Defence Coaching Institute in Pathankot, NEET coaching Pathankot, JEE coaching Pathankot",
        canonical: "/",
        jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
                {
                    "@type": "EducationalOrganization",
                    name: "Dacron Classes",
                    url: SITE.domain,
                    telephone: SITE.phoneRaw,
                    email: SITE.email,
                    address: {
                        "@type": "PostalAddress",
                        streetAddress: "Main Bazar Road, Near Nangal Chowk",
                        addressLocality: "Pathankot",
                        addressRegion: "Punjab",
                        postalCode: "145001",
                        addressCountry: "IN",
                    },
                    geo: {
                        "@type": "GeoCoordinates",
                        latitude: 32.2746,
                        longitude: 75.6522,
                    },
                    aggregateRating: {
                        "@type": "AggregateRating",
                        ratingValue: "4.9",
                        reviewCount: "287",
                    },
                },
                {
                    "@type": "FAQPage",
                    mainEntity: FAQS.map((f) => ({
                        "@type": "Question",
                        name: f.q,
                        acceptedAnswer: { "@type": "Answer", text: f.a },
                    })),
                },
            ],
        },
    });

    return (
        <main data-testid="home-page">
            {/* HERO */}
            <section
                data-testid="hero-section"
                className="relative bg-brand-navy text-white overflow-hidden"
            >
                <div
                    className="absolute inset-0 opacity-25 bg-cover bg-center"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1748334118478-d276d4c0a834?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjBzdHVkZW50cyUyMHN0dWR5aW5nJTIwbGlicmFyeXxlbnwwfHx8fDE3NzcyMDUyNjl8MA&ixlib=rb-4.1.0&q=85)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" />

                <div className="container-x relative pt-16 md:pt-24 pb-20 md:pb-32 grid lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-7 animate-fade-up">
                        <div className="inline-flex items-center gap-2 border border-white/20 px-3 py-1.5 mb-6 text-xs tracking-[0.22em] uppercase">
                            <span className="w-1.5 h-1.5 bg-brand-gold rounded-full animate-pulse" />
                            Admissions Open · Batch 2026
                        </div>
                        <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-balance">
                            Pathankot's
                            <br />
                            <span className="text-brand-gold">#1 Coaching</span>
                            <br />
                            for Defence, NEET & JEE.
                        </h1>
                        <p className="mt-7 text-base md:text-lg text-white/80 max-w-xl leading-relaxed">
                            Sainik School, NDA, RIMC, RMS, NEET, JEE & tuitions for Class
                            6-12 — all under one roof. 100+ selections, ex-officer mentors,
                            NTA-pattern mocks, NCERT-rooted teaching.
                        </p>

                        <div className="mt-9 flex flex-col sm:flex-row gap-3">
                            <Button
                                asChild
                                data-testid="hero-cta-demo"
                                className="rounded-none h-14 px-8 bg-brand-crimson hover:bg-brand-crimsonHover font-heading font-bold tracking-wide text-base"
                            >
                                <a href="#enquiry">Book Free Demo Class</a>
                            </Button>
                            <Button
                                asChild
                                data-testid="hero-cta-enquire"
                                variant="outline"
                                className="rounded-none h-14 px-8 border-white text-white hover:bg-white hover:text-brand-navy font-heading font-semibold text-base bg-transparent"
                            >
                                <a href={waLink()} target="_blank" rel="noopener noreferrer">
                                    Enquire on WhatsApp
                                </a>
                            </Button>
                        </div>

                        <div className="mt-10 flex flex-wrap gap-x-8 gap-y-4 text-sm">
                            {STATS.map((s) => (
                                <div key={s.label}>
                                    <div className="font-heading font-black text-3xl text-brand-gold">
                                        {s.num}
                                    </div>
                                    <div className="text-xs text-white/70 tracking-wider uppercase">
                                        {s.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-5 lg:pl-8" id="enquiry">
                        <EnquiryForm sourcePage="homepage-hero" />
                    </div>
                </div>

                {/* Marquee */}
                <div className="relative bg-brand-crimson text-white border-y border-black/10 overflow-hidden">
                    <div className="flex whitespace-nowrap animate-marquee py-3 text-xs sm:text-sm font-heading font-semibold tracking-[0.2em] uppercase">
                        {Array(2)
                            .fill([
                                "★ AISSEE 2025 — 28 Selections",
                                "★ NDA Written 2025 — 16 Recommended",
                                "★ NEET 2025 — 11 Above 600",
                                "★ JEE Main 2025 — 99.1+ Percentile",
                                "★ RIMC 2025 — 4 Cadets Selected",
                                "★ Class 10 CBSE — 100% Above 80%",
                            ])
                            .flat()
                            .map((t, i) => (
                                <span key={i} className="px-8 inline-flex items-center gap-3">
                                    {t}
                                </span>
                            ))}
                    </div>
                </div>
            </section>

            {/* COURSES BENTO */}
            <section
                data-testid="courses-section"
                className="section-y bg-white"
                id="courses"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-8 mb-14 items-end">
                        <div className="lg:col-span-7">
                            <div className="overline text-brand-crimson mb-3">
                                Top Courses · Pathankot
                            </div>
                            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05]">
                                Every defence, medical and engineering goal —
                                <span className="text-brand-crimson"> taught right here in Pathankot.</span>
                            </h2>
                        </div>
                        <p className="lg:col-span-5 text-brand-sub leading-relaxed">
                            From AISSEE & NDA to NEET, JEE and Class 6-12 tuitions, our
                            programmes are calibrated for Pathankot students — small
                            batches, NTA-pattern mocks, NCERT-rooted teaching.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
                        {COURSES.map((c, i) => {
                            const span =
                                i === 0
                                    ? "md:col-span-7"
                                    : i === 1
                                    ? "md:col-span-5"
                                    : i === 4
                                    ? "md:col-span-5"
                                    : i === 5
                                    ? "md:col-span-7"
                                    : i === 6
                                    ? "md:col-span-7"
                                    : i === 7
                                    ? "md:col-span-5"
                                    : "md:col-span-4";
                            return (
                                <Link
                                    to={`/${c.slug}`}
                                    key={c.slug}
                                    data-testid={`course-card-${c.slug}`}
                                    className={`group ${span} bg-white border border-black/10 p-7 md:p-8 hover:-translate-y-1 hover:shadow-[0_24px_48px_-24px_rgba(10,25,47,0.25)] transition-all relative overflow-hidden`}
                                >
                                    <div className="flex items-start justify-between gap-4 mb-6">
                                        <div className="overline text-brand-crimson">
                                            {c.category}
                                        </div>
                                        <ChevronRight className="w-5 h-5 text-brand-navy group-hover:translate-x-1 transition-transform" />
                                    </div>
                                    <h3 className="font-heading font-extrabold text-xl md:text-2xl text-brand-navy leading-tight mb-3">
                                        {c.short} <span className="text-brand-sub font-medium">in Pathankot</span>
                                    </h3>
                                    <p className="text-sm text-brand-sub leading-relaxed line-clamp-3">
                                        {c.hero}
                                    </p>
                                    <div className="mt-6 inline-flex items-center text-xs font-semibold tracking-[0.2em] uppercase text-brand-navy group-hover:text-brand-crimson">
                                        Explore Programme
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHY US */}
            <section
                data-testid="why-us-section"
                id="why-us"
                className="section-y bg-brand-surface"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
                        <div className="lg:col-span-7">
                            <div className="overline text-brand-crimson mb-3">
                                Why Choose Dacron
                            </div>
                            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05]">
                                Six reasons parents in Pathankot pick us — and stay.
                            </h2>
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border border-black/10 bg-white">
                        {WHY_US.map((w, i) => {
                            const Icon = w.icon;
                            return (
                                <div
                                    key={w.title}
                                    data-testid={`why-card-${i}`}
                                    className="p-8 border-b lg:border-b-0 lg:border-r border-black/10 last:border-r-0 [&:nth-child(2n)]:lg:border-r [&:nth-child(3n)]:lg:border-r-0 last-of-type:border-b-0 hover:bg-brand-surface transition-colors"
                                >
                                    <Icon className="w-9 h-9 text-brand-crimson mb-5" strokeWidth={1.5} />
                                    <h3 className="font-heading font-extrabold text-lg text-brand-navy mb-2.5">
                                        {w.title}
                                    </h3>
                                    <p className="text-sm text-brand-sub leading-relaxed">
                                        {w.body}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section
                data-testid="testimonials-section"
                id="testimonials"
                className="section-y bg-white"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
                        <div className="lg:col-span-7">
                            <div className="overline text-brand-crimson mb-3">
                                Student Success Stories
                            </div>
                            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05]">
                                Real students. Real selections. <br />
                                <span className="text-brand-sub">From classrooms in Pathankot.</span>
                            </h2>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {TESTIMONIALS.map((t, i) => (
                            <div
                                key={i}
                                data-testid={`testimonial-${i}`}
                                className="bg-white border border-black/10 p-8 md:p-10 hover:shadow-[0_24px_48px_-24px_rgba(10,25,47,0.2)] transition-shadow"
                            >
                                <Quote className="w-8 h-8 text-brand-crimson mb-5" />
                                <p className="text-base md:text-lg text-brand-ink leading-relaxed mb-7">
                                    "{t.text}"
                                </p>
                                <div className="flex items-center justify-between border-t border-black/10 pt-5">
                                    <div>
                                        <div className="font-heading font-extrabold text-brand-navy">
                                            {t.name}
                                        </div>
                                        <div className="text-xs text-brand-crimson tracking-wider uppercase mt-1">
                                            {t.result}
                                        </div>
                                    </div>
                                    <BadgeCheck className="w-7 h-7 text-brand-gold" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GOOGLE REVIEWS */}
            <section
                data-testid="reviews-section"
                className="section-y bg-brand-navy text-white relative overflow-hidden"
            >
                <div className="container-x">
                    <div className="grid lg:grid-cols-12 gap-10 mb-12 items-end">
                        <div className="lg:col-span-7">
                            <div className="overline text-brand-gold mb-3">
                                Google Reviews · 4.9★
                            </div>
                            <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                                What 287+ parents say on Google.
                            </h2>
                        </div>
                        <div className="lg:col-span-5 flex lg:justify-end">
                            <a
                                href={SITE.gbpUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="see-all-reviews"
                                className="inline-flex items-center gap-2 border border-white/30 px-5 py-3 text-sm font-heading font-semibold tracking-wide hover:bg-white hover:text-brand-navy transition-colors"
                            >
                                See all on Google
                                <ChevronRight className="w-4 h-4" />
                            </a>
                        </div>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {REVIEWS.map((r, i) => (
                            <div
                                key={i}
                                data-testid={`review-${i}`}
                                className="bg-white/5 border border-white/10 p-6 backdrop-blur-sm"
                            >
                                <div className="flex gap-0.5 mb-4">
                                    {Array(r.stars)
                                        .fill(0)
                                        .map((_, j) => (
                                            <Star
                                                key={j}
                                                className="w-4 h-4 fill-brand-gold text-brand-gold"
                                            />
                                        ))}
                                </div>
                                <p className="text-sm text-white/85 leading-relaxed mb-5">
                                    "{r.text}"
                                </p>
                                <div className="font-heading font-semibold text-sm text-brand-gold">
                                    {r.name}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section
                data-testid="faq-section"
                id="faq"
                className="section-y bg-white"
            >
                <div className="container-x grid lg:grid-cols-12 gap-12">
                    <div className="lg:col-span-5">
                        <div className="overline text-brand-crimson mb-3">FAQ</div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05] mb-6">
                            Questions parents in Pathankot ask us.
                        </h2>
                        <p className="text-brand-sub leading-relaxed mb-8">
                            Couldn't find your answer? Call us at{" "}
                            <a
                                href={telLink}
                                className="text-brand-crimson font-semibold underline-offset-4 hover:underline"
                            >
                                {SITE.phone}
                            </a>{" "}
                            or message on WhatsApp — we reply within minutes.
                        </p>
                        <Button
                            asChild
                            data-testid="faq-cta"
                            className="rounded-none h-12 px-7 bg-brand-navy hover:bg-brand-navyHover font-heading font-semibold"
                        >
                            <a href="#enquiry">Book a Free Demo</a>
                        </Button>
                    </div>
                    <div className="lg:col-span-7">
                        <Accordion type="single" collapsible className="border-t border-black/10">
                            {FAQS.map((f, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`f-${i}`}
                                    className="border-b border-black/10"
                                    data-testid={`faq-item-${i}`}
                                >
                                    <AccordionTrigger className="text-left font-heading font-semibold text-brand-navy text-base md:text-lg py-5 hover:no-underline">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-brand-sub leading-relaxed pb-5 pr-4">
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                </div>
            </section>

            {/* CONTACT + MAP */}
            <section
                data-testid="contact-section"
                id="contact"
                className="bg-brand-surface"
            >
                <div className="grid lg:grid-cols-2">
                    <div className="p-10 md:p-16 lg:p-20">
                        <div className="overline text-brand-crimson mb-3">
                            Visit Us · Pathankot
                        </div>
                        <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl text-brand-navy tracking-tight leading-[1.05] mb-6">
                            Walk in. Tour the campus. Meet our mentors.
                        </h2>
                        <p className="text-brand-sub leading-relaxed mb-10">
                            Our institute is located in the heart of Pathankot — easily
                            accessible from Mamoon, Dhangu Road, Sujanpur and Gurdaspur.
                        </p>

                        <div className="space-y-5 mb-10">
                            <a
                                href={telLink}
                                data-testid="contact-call"
                                className="flex items-start gap-4 p-5 bg-white border border-black/10 hover:border-brand-crimson transition-colors group"
                            >
                                <Phone className="w-6 h-6 text-brand-crimson shrink-0 mt-0.5" />
                                <div>
                                    <div className="overline text-brand-sub mb-1">
                                        Call / WhatsApp
                                    </div>
                                    <div className="font-heading font-extrabold text-xl text-brand-navy group-hover:text-brand-crimson">
                                        {SITE.phone}
                                    </div>
                                </div>
                            </a>
                            <a
                                href={mailLink}
                                data-testid="contact-email"
                                className="flex items-start gap-4 p-5 bg-white border border-black/10 hover:border-brand-crimson transition-colors group"
                            >
                                <Mail className="w-6 h-6 text-brand-crimson shrink-0 mt-0.5" />
                                <div>
                                    <div className="overline text-brand-sub mb-1">Email</div>
                                    <div className="font-heading font-extrabold text-xl text-brand-navy group-hover:text-brand-crimson">
                                        {SITE.email}
                                    </div>
                                </div>
                            </a>
                            <div className="flex items-start gap-4 p-5 bg-white border border-black/10">
                                <MapPin className="w-6 h-6 text-brand-crimson shrink-0 mt-0.5" />
                                <div>
                                    <div className="overline text-brand-sub mb-1">Address</div>
                                    <div className="font-heading font-semibold text-base text-brand-navy">
                                        {SITE.address}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <Button
                            asChild
                            data-testid="contact-cta-whatsapp"
                            className="rounded-none h-12 px-7 bg-brand-whatsapp hover:bg-brand-whatsapp/90 font-heading font-semibold"
                        >
                            <a href={waLink()} target="_blank" rel="noopener noreferrer">
                                Quick WhatsApp Enquiry
                            </a>
                        </Button>
                    </div>

                    <div className="min-h-[420px] lg:min-h-full bg-brand-navy relative">
                        <iframe
                            data-testid="google-map"
                            title="Dacron Classes Pathankot location"
                            src={SITE.mapEmbed}
                            width="100%"
                            height="100%"
                            style={{ border: 0, minHeight: "420px" }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="absolute inset-0 w-full h-full"
                        />
                    </div>
                </div>
            </section>

            {/* LOCAL SEO RICH FOOTER BLOCK */}
            <section className="bg-white border-t border-black/10">
                <div className="container-x py-16">
                    <div className="overline text-brand-crimson mb-4">
                        Coaching in Pathankot · Trusted Since 2013
                    </div>
                    <h2 className="font-heading font-black text-2xl md:text-3xl text-brand-navy mb-5 max-w-3xl">
                        The complete coaching ecosystem for students in Pathankot, Punjab.
                    </h2>
                    <div className="grid md:grid-cols-2 gap-x-12 gap-y-4 text-brand-sub leading-relaxed text-sm md:text-base">
                        <p>
                            Whether you're searching for the{" "}
                            <strong className="text-brand-navy">
                                best Sainik School coaching in Pathankot
                            </strong>
                            , the{" "}
                            <strong className="text-brand-navy">
                                top NDA coaching in Pathankot
                            </strong>{" "}
                            or simply the{" "}
                            <strong className="text-brand-navy">
                                most reliable tuition centre in Pathankot
                            </strong>
                            , Dacron Classes has a programme purpose-built for your
                            child's goals.
                        </p>
                        <p>
                            We serve aspirants across Pathankot, Mamoon, Dhangu Road,
                            Sujanpur, Gurdaspur, Dinanagar and surrounding areas. Our
                            faculty includes ex-defence officers and IIT/NIT alumni who
                            have helped 100+ students crack AISSEE, NDA, RIMC, RMS, NEET
                            and JEE in the last decade.
                        </p>
                    </div>
                </div>
            </section>
        </main>
    );
}
